import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import doubleChevronsIcon from '../OrbusStreamNavigatorComponent/assets/icons/double chevrons.svg';

interface TimeSeriesChartProps {
  metricKey: string;
  title: string;
  description?: string;
  formatValue?: (value: number) => string;
  lineColor?: string;
  compact?: boolean;
  data?: [number, number][];
  isLoading?: boolean;
  targetValue?: number;
  targetArray?: Array<{ startTs: number; endTs: number; value: number }>;
  metricOperation?: string;
  decreasingTarget?: boolean | null;
  alertingThresholdPercentage?: number | null;
}

// Format timestamp to readable date
const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp * 1000); // Convert seconds to milliseconds
  return date.toLocaleString('default', { month: 'short', year: 'numeric' });
};

// Get default formatter based on metric type
const getDefaultFormatter = (metricKey: string): (value: number) => string => {
  switch (metricKey) {
    case 'total_revenue':
    case 'average_deal_size':
      return (value: number) => new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    case 'win_rate':
    case 'conversion_rate':
      return (value: number) => value.toFixed(1);
    case 'avg_sales_cycle':
      return (value: number) => value.toFixed(1);
    default:
      return (value: number) => value.toLocaleString();
  }
};

export const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  metricKey,
  title,
  description,
  formatValue,
  lineColor = '#acb3ffff',
  compact = false,
  data = [],
  metricOperation,
  targetValue,
  targetArray,
  decreasingTarget,
  alertingThresholdPercentage
}) => {
  const [zoomLevel, setZoomLevel] = React.useState(0);
  const [windowStartIndex, setWindowStartIndex] = React.useState(0);
  const firstTimestamp = data[0]?.[0];
  const lastTimestamp = data[data.length - 1]?.[0];

  React.useEffect(() => {
    setZoomLevel(0);
    setWindowStartIndex(0);
  }, [metricKey, data.length, firstTimestamp, lastTimestamp]);

  try {
    if (metricKey === 'leads_by_orgunit') {
      console.debug(`[leads_by_orgunit TimeSeriesChart] targetArray received: segments=%d, first 3:`, targetArray?.length ?? 0, targetArray?.slice(0, 3));
      console.debug(`[leads_by_orgunit TimeSeriesChart] data points=%d, first 3:`, data.length, data.slice(0, 3));
    }
    // Use target segments as-is — their timestamps already match the data resolution.
    // normaliseToMonthStart would collapse daily/weekly segments to zero width.
    const normalisedTargetArray = targetArray;

    // Transform the data for Recharts – keep original timestamps so each
    // data point (daily, weekly, etc.) gets its own bar
    const dataForChart = data
      .filter(point => Array.isArray(point) && point.length === 2) // Ensure valid data points
      .map(point => ({
        timestamp: point[0],
        value: point[1]
      }))
      .sort((a, b) => a.timestamp - b.timestamp); // Sort by date

    const op = (metricOperation || '').toLowerCase();
    const opAverage = op === 'average';
    const opTotal = op === 'total';
    const allowOpCumulative = opAverage || opTotal;

    // Calculate cumulative values that reset at the start of each year
    let cumulativeValue = 0;
    let currentYear: number | null = null;
    let yearSum = 0;
    let yearCount = 0;

    // Separate arrays: one for value line (original data), one for cumulative line (with synthetic points)
    const dataWithCumulative: Array<{ timestamp: number; value: number; cumulativeValue: number }> = [];
    const cumulativeData: Array<{ timestamp: number; cumulativeValue: number }> = [];

    dataForChart.forEach((point) => {
      const pointDate = new Date(point.timestamp * 1000);
      const pointYear = pointDate.getFullYear();

      // Reset cumulative when year changes – start from the first value
      // of the new period rather than dropping to zero
      if (currentYear !== null && pointYear !== currentYear) {
        if (opAverage) {
          yearSum = 0;
          yearCount = 0;
        }
        cumulativeValue = 0;
      }
      currentYear = pointYear;

      if (opAverage) {
        yearSum += point.value;
        yearCount += 1;
        cumulativeValue = yearCount > 0 ? yearSum / yearCount : 0;
      } else {
        cumulativeValue += point.value;
      }

      // Add to both arrays
      dataWithCumulative.push({
        ...point,
        cumulativeValue
      });

      cumulativeData.push({
        timestamp: point.timestamp,
        cumulativeValue
      });
    });

    // If no data points, show a message or return null in compact mode
    if (dataForChart.length === 0) {
      if (compact) return null;
      return (
        <Card className="w-full h-full">
          <CardHeader>
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
          <CardContent>
            <div className="text-center p-8 text-muted-foreground">
              No data available for this metric.
            </div>
          </CardContent>
        </Card>
      );
    }

    // Merge both datasets for the chart, using all unique timestamps
    const allTimestamps = Array.from(
      new Set([...dataWithCumulative.map(d => d.timestamp), ...cumulativeData.map(d => d.timestamp)])
    ).sort((a, b) => a - b);

    const mergedData = allTimestamps.map(timestamp => {
      const valuePoint = dataWithCumulative.find(d => d.timestamp === timestamp);
      const cumulativePoint = cumulativeData.find(d => d.timestamp === timestamp);

      return {
        timestamp,
        value: valuePoint?.value ?? null,
        cumulativeValue: cumulativePoint?.cumulativeValue ?? null
      };
    });

    // Zoom affects both visible x-range and y-axis domain (rescaled to zoomed window).
    const minVisiblePoints = compact ? 4 : 6;
    const maxZoomLevel = mergedData.length > minVisiblePoints
      ? Math.ceil(Math.log2(mergedData.length / minVisiblePoints))
      : 0;
    const effectiveZoomLevel = Math.min(zoomLevel, maxZoomLevel);

    const visibleCount = Math.max(
      minVisiblePoints,
      Math.ceil(mergedData.length / Math.pow(2, effectiveZoomLevel))
    );
   
    // Aggressive scaling: low zoom should make the thumb almost fill the slider.
    // const visibleRatio = mergedData.length > 0 ? visibleCount / mergedData.length : 1;
    //const sliderThumbWidth = `${Math.round(20 + 75 * Math.pow(visibleRatio, 1.8))}%`;

    const sliderThumbWidth = `8%`;
    const maxStartIndex = Math.max(0, mergedData.length - visibleCount);
    const effectiveStartIndex = Math.min(windowStartIndex, maxStartIndex);
    const zoomedData = mergedData.slice(effectiveStartIndex, effectiveStartIndex + visibleCount);

    const changeZoomLevel = (direction: 1 | -1) => {
      const nextZoomLevel = Math.min(Math.max(effectiveZoomLevel + direction, 0), maxZoomLevel);
      const nextVisibleCount = Math.max(
        minVisiblePoints,
        Math.ceil(mergedData.length / Math.pow(2, nextZoomLevel))
      );
      const nextMaxStartIndex = Math.max(0, mergedData.length - nextVisibleCount);
      const currentCenter = effectiveStartIndex + Math.floor(visibleCount / 2);
      const nextStartIndex = Math.min(
        Math.max(0, currentCenter - Math.floor(nextVisibleCount / 2)),
        nextMaxStartIndex
      );

      setZoomLevel(nextZoomLevel);
      setWindowStartIndex(nextStartIndex);
    };

    // Determine if resolution is equal or coarser than yearly by checking median gap
    const valueTimestamps = mergedData
      .filter(d => d.value !== null)
      .map(d => d.timestamp)
      .sort((a, b) => a - b);
    let hideCumulativeAndTarget = false;
    if (valueTimestamps.length > 1) {
      const diffs = [] as number[];
      for (let i = 1; i < valueTimestamps.length; i++) {
        diffs.push(valueTimestamps[i] - valueTimestamps[i - 1]);
      }
      diffs.sort((a, b) => a - b);
      const median = diffs[Math.floor(diffs.length / 2)] || diffs[0];
      const YEAR_SECONDS = 365 * 24 * 60 * 60;
      // If median spacing is roughly a year or more, treat as yearly aggregation
      hideCumulativeAndTarget = median >= YEAR_SECONDS * 0.9;
    }
    const showCumulativeAndTargets = !hideCumulativeAndTarget && allowOpCumulative;

    // Calculate min and max for proper axis scaling (from visible data so bars scale with zoom)
    const values = zoomedData.map(item => item.value).filter((v): v is number => v !== null);
    const cumulativeValues = showCumulativeAndTargets
      ? zoomedData.map(item => item.cumulativeValue).filter((v): v is number => v !== null)
      : [];
    const allValues = [...values, ...cumulativeValues];

    let max = allValues.length > 0 ? Math.max(...allValues) : 0;
    let min = values.length > 0 ? Math.min(...values) : 0;

    // Compute adaptive padding based on the median gap between data points
    // so the domain scales correctly for daily, weekly, monthly, yearly, etc.
    const sortedTs = zoomedData.map(d => d.timestamp).sort((a, b) => a - b);
    let domainPad = 15 * 24 * 60 * 60; // fallback ~15 days
    if (sortedTs.length > 1) {
      const gaps = sortedTs.slice(1).map((t, i) => t - sortedTs[i]);
      gaps.sort((a, b) => a - b);
      domainPad = Math.round((gaps[Math.floor(gaps.length / 2)] || gaps[0]) / 2);
    }
    const domainStart = (zoomedData[0]?.timestamp ?? 0) - domainPad;
    const domainEnd = (zoomedData[zoomedData.length - 1]?.timestamp ?? domainStart) + domainPad;

    // Deduplicate ticks: keep only one timestamp per unique formatted label
    // so daily data doesn't repeat "Nov 2025" 30 times
    const seenLabels = new Set<string>();
    const explicitTicks = zoomedData
      .map(d => d.timestamp)
      .filter(ts => {
        const label = formatTimestamp(ts);

        if (seenLabels.has(label)) return false;
        seenLabels.add(label);
        return true;
      });

    // Include target values in domain calculation only if we're showing them
    if (showCumulativeAndTargets) {
      if (targetValue !== undefined) {
        max = Math.max(max, targetValue);
        min = Math.min(min, targetValue);
      }
      if (normalisedTargetArray && normalisedTargetArray.length > 0) {
        const targetValues = normalisedTargetArray.map(t => t.value);
        max = Math.max(max, ...targetValues);
        min = Math.min(min, ...targetValues);
      }
    }

    // Padding for y-axis
    const padding = (max - min) * 0.1; // 10% padding

    // Get appropriate formatter for the metric
    const defaultFormatter = formatValue || getDefaultFormatter(metricKey);

    // Custom tooltip formatter to show additional information
    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const value = payload[0].value;
        const formattedValue = defaultFormatter(value);
        const payloadTimestamp = payload[0].payload.timestamp;
        const cumulativeValue = payload[0].payload.cumulativeValue;
        const formattedCumulative = cumulativeValue != null ? defaultFormatter(cumulativeValue) : '';

        const label = payloadTimestamp ? formatTimestamp(payloadTimestamp) : '';

        // Target is taken from the segment closest to this timestamp in the past
        // (largest startTs that is <= payloadTimestamp), matching getCurrentPeriodTarget
        let relevantTarget = targetValue;
        if (normalisedTargetArray && payloadTimestamp !== undefined) {
          let best: { startTs: number; endTs: number; value: number } | undefined;
          for (const seg of normalisedTargetArray) {
            if (seg.startTs <= payloadTimestamp && (!best || seg.startTs > best.startTs)) {
              best = seg;
            }
          }
          relevantTarget = best?.value;
        }

        return (
          <div className="bg-background border rounded p-2 shadow-sm">
            <p className="text-sm font-medium">{label}</p>
            <p className="text-sm text-muted-foreground">{`Value: ${formattedValue}`}</p>
            {showCumulativeAndTargets && (
              <>
                <p className="text-sm text-muted-foreground">{`Cumulative (Year): ${formattedCumulative}`}</p>
                {relevantTarget !== undefined && (
                  <p className="text-sm text-muted-foreground">{`Target: ${defaultFormatter(relevantTarget)}`}</p>
                )}
                {relevantTarget !== undefined && cumulativeValue != null && (() => {
                  const offset = alertingThresholdPercentage ?? 0;
                  const adjustedActual = offset && cumulativeValue < offset ? 1 : cumulativeValue - offset;
                  const adjustedTarget = relevantTarget - offset;
                  const rawPct = adjustedTarget > 0
                    ? (adjustedActual / adjustedTarget) * 100
                    : (cumulativeValue / relevantTarget) * 100;
                  const pct = Math.round(
                    decreasingTarget ? (100 / rawPct) * 100 : rawPct
                  );
                  return (
                    <p className="text-sm text-muted-foreground">
                      {pct === 100 ? 'At target (100%)' : `${pct}% of target`}
                    </p>
                  );
                })()}
              </>
            )}
          </div>
        );
      }
      return null;
    };

    const chart = (
      <div className="w-full h-full flex flex-col">
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={zoomedData} margin={compact ? { top: 5, right: 24, left: 25, bottom: 5 } : { top: 5, right: 56, left: 40, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={compact ? "#eee" : undefined} />
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={[domainStart, domainEnd]}
                ticks={explicitTicks}
                tickFormatter={(value) => formatTimestamp(value)}
                tick={{ fontSize: compact ? 11 : 13 }}
                tickLine={!compact}
                axisLine={!compact}
              />
              <YAxis
                domain={[Math.max(0, min - padding), max + padding]}
                tickFormatter={defaultFormatter}
                tick={{ fontSize: compact ? 11 : 13 }}
                tickLine={!compact}
                axisLine={!compact}
                width={compact ? 45 : 70}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="value"
                name={title || 'Value'}
                fill={lineColor}
                fillOpacity={0.7}
                animationDuration={1500}
              />
              {showCumulativeAndTargets && (
                <Line
                  type="monotone"
                  dataKey="cumulativeValue"
                  name="Cumulative (Year)"
                  stroke="#82ca9d"
                  strokeWidth={compact ? 2.5 : 3}
                  dot={false}
                  activeDot={{ r: compact ? 4 : 8 }}
                  animationDuration={1500}
                />
              )}
              {showCumulativeAndTargets && normalisedTargetArray && normalisedTargetArray.map((target, index) => {
                const segStart = target.startTs;
                const segEnd = target.endTs;

                const clampedStart = Math.max(segStart, domainStart);
                const clampedEnd = Math.min(segEnd, domainEnd);
                if (clampedStart >= clampedEnd) return null;

                const next = normalisedTargetArray[index + 1];
                const boundaryTs = next ? next.startTs : null;
                const renderStep = next && boundaryTs !== null && boundaryTs >= domainStart && boundaryTs <= domainEnd && next.value !== target.value;

                return (
                  <React.Fragment key={`target-frag-${index}-${segStart}`}>
                    <ReferenceLine
                      key={`target-${index}`}
                      stroke="#ff7300"
                      strokeDasharray="3 3"
                      segment={[
                        { x: clampedStart, y: target.value },
                        { x: clampedEnd, y: target.value }
                      ]}
                    />
                    {renderStep && (
                      <ReferenceLine
                        key={`target-step-${index}`}
                        stroke="#ff7300"
                        strokeDasharray="3 3"
                        segment={[
                          { x: boundaryTs!, y: target.value },
                          { x: boundaryTs!, y: next!.value }
                        ]}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {maxZoomLevel > 0 && (
          <div className={compact ? 'pt-1 px-1' : 'pt-2 px-1'}>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => changeZoomLevel(-1)}
                disabled={effectiveZoomLevel === 0}
                className="h-6 min-w-6 rounded border border-input bg-background px-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Zoom out"
              >
                -
              </button>
              <button
                type="button"
                onClick={() => changeZoomLevel(1)}
                disabled={effectiveZoomLevel >= maxZoomLevel}
                className="h-6 min-w-6 rounded border border-input bg-background px-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Zoom in"
              >
                +
              </button>
              {effectiveZoomLevel > 0 && maxStartIndex > 0 && (
                <input
                  type="range"
                  min={0}
                  max={maxStartIndex}
                  step={1}
                  value={effectiveStartIndex}
                  onChange={(event) => setWindowStartIndex(Number(event.target.value))}
                  style={{
                    ['--slider-thumb-width' as string]: sliderThumbWidth,
                    ['--slider-thumb-icon' as string]: `url("${doubleChevronsIcon}")`,
                  }}
                  className="flex-1 min-w-0 h-[25px] cursor-pointer appearance-none bg-[#f8f9fa] outline-none opacity-70 transition-opacity duration-200 hover:opacity-100 [&::-webkit-slider-thumb]:h-[25px] [&::-webkit-slider-thumb]:w-[var(--slider-thumb-width)] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:bg-[#acb9ffff] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:bg-no-repeat [&::-webkit-slider-thumb]:bg-center [&::-webkit-slider-thumb]:[background-size:16px_16px] [&::-webkit-slider-thumb]:[background-image:var(--slider-thumb-icon)] [&::-moz-range-track]:h-[25px] [&::-moz-range-track]:bg-[#f8f9fa] [&::-moz-range-track]:border-0 [&::-moz-range-thumb]:h-[25px] [&::-moz-range-thumb]:w-[var(--slider-thumb-width)] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[#acb9ffff] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:bg-no-repeat [&::-moz-range-thumb]:bg-center [&::-moz-range-thumb]:[background-size:16px_16px] [&::-moz-range-thumb]:[background-image:var(--slider-thumb-icon)]"
                  aria-label="Slide through zoomed time range"
                />
              )}
            </div>
          </div>
        )}
      </div>
    );

    if (compact) {
      return chart;
    }

    return (
      <Card className="w-full h-full">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="h-80">
            {chart}
          </div>
        </CardContent>
      </Card>
    );
  } catch (error) {
    console.error('Error rendering TimeSeriesChart:', error);
    if (compact) return null;
    return (
      <Card className="w-full h-full">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-center p-8 text-muted-foreground">
            An error occurred while rendering the chart. Please try again later.
          </div>
        </CardContent>
      </Card>
    );
  }
};